import { useEffect, useState } from 'react'
import { connect, useDispatch, useSelector } from 'react-redux'
import moment from 'moment'
import { isEmpty, isNil } from 'lodash'
import { Link, useLocation, useParams } from 'react-router-dom'

import routes, { categoriesGroup } from '../../routers'
import { getAdvertising } from '../../api/admin'
import { getRecipe, getRecipeByUserIdGroupId, getVideoRecipe } from '../../api/recipe'
import { setLoading } from '../../redux/actions/ui'
import {
  ACTIVE_STATUS,
  MIN_LAST_PAGE,
  PAGINATION_SIZE,
  ROLE_USER_ON_GROUP,
  isFreePlan,
} from '../../utils/constants'
import ListRecipes, { LockIcon } from '../../components/ListRecipes'
import UserPhoto from '../../components/UserPhoto'
import Layout from '../../components/Layout'
import { BookProgress } from '../../components/CookbookPromo'
import FirstRecipeStart from '../../components/FirstRecipeStart'

import './styles.css'

const Profile = ({ user }) => {
  const dispatch = useDispatch()
  const { idUser, idGroup, idSlug } = useParams()
  // Just published a recipe: show how close the family is to a printed cookbook.
  // Just confirmed their email: welcome them.
  const { savedRecipe, welcome } = useLocation().state || {}
  const groups = useSelector((state) => state.groups?.data)
  const [recipeData, setRecipeData] = useState([])
  const [recipesLoaded, setRecipesLoaded] = useState(false)
  const [visibleLoad, setVisibleLoad] = useState(false)
  const [advertising, setAdvertising] = useState([])
  const [userData, setUserData] = useState({})
  const [page, setPage] = useState(1)
  const [videoData, setVideoData] = useState(null)

  const checkLinkToRecipe = (idRecipe, slug) => {
    return !isNil(idUser)
      ? `${categoriesGroup(idSlug, idGroup)}/all/${slug}-${idRecipe}`
      : `/${routes.dashboard}/${slug}-${idRecipe}`
  }

  useEffect(() => {
    dispatch(setLoading(true))
    getAdvertising()
      .then(({ data }) => setAdvertising(data))
      .then(getVideoRecipe)
      .then(({ data }) => setVideoData(data))
      .catch((e) => console.log(e))
    if (isNil(idUser)) {
      return getRecipe(ACTIVE_STATUS, { page, size: PAGINATION_SIZE })
        .then(({ data }) => {
          const allRecipes = data?.data
          setUserData(user)
          setRecipeData(allRecipes)
          setVisibleLoad(allRecipes?.length >= PAGINATION_SIZE && data?.lastPage !== MIN_LAST_PAGE)
        })
        .catch((e) => console.log(e))
        .finally(() => {
          setRecipesLoaded(true)
          dispatch(setLoading(false))
        })
    }
    getRecipeByUserIdGroupId(idUser, idGroup)
      .then(({ data }) => {
        const allRecipes = data?.recipes?.data
        setUserData(data?.user)
        setRecipeData(allRecipes)
        setVisibleLoad(allRecipes?.length >= PAGINATION_SIZE && data?.lastPage !== MIN_LAST_PAGE)
      })
      .catch((e) => console.log(e))
      .finally(() => dispatch(setLoading(false)))
  }, [])

  const paginateRecipes = () => {
    setPage((oldState) => oldState + 1)
    dispatch(setLoading(true))
    if (isNil(idUser)) {
      return getRecipe(ACTIVE_STATUS, { page: page + 1, size: PAGINATION_SIZE })
        .then(({ data }) => {
          const allRecipes = data?.data
          setRecipeData((oldState) => [...oldState, ...allRecipes])
          setVisibleLoad(allRecipes?.length >= PAGINATION_SIZE && data?.lastPage !== MIN_LAST_PAGE)
        })
        .catch((e) => console.log(e))
        .finally(() => dispatch(setLoading(false)))
    }
    getRecipeByUserIdGroupId(idUser, idGroup)
      .then(({ data }) => {
        const allRecipes = data?.recipes?.data
        setRecipeData((oldState) => [...oldState, ...allRecipes])
        setVisibleLoad(allRecipes?.length >= PAGINATION_SIZE && data?.lastPage !== MIN_LAST_PAGE)
      })
      .catch((e) => console.log(e))
      .finally(() => dispatch(setLoading(false)))
  }

  const ownProfile = isNil(idUser)
  const noRecipesYet = ownProfile && recipesLoaded && isEmpty(recipeData)
  // A family they joined from an invite (not one they started), to look at first
  const invitedFamily = (groups || []).find((group) => group?.role === ROLE_USER_ON_GROUP) || null

  const UserInfoBlock = () => {
    return (
      <div className={`innerUsers ${isNil(idUser) ? 'ownProfileInfo' : ''}`}>
        <h2 className="titleTableUsers">{userData?.fullname}</h2>
        <UserPhoto image={userData?.avatar} classStylePhoto="userProfilePhoto" />
        {!isNil(idUser) ? (
          <>
            <p className="textProfileBlock joinedText">
              Joined group in: {moment(userData?.joined_at).format('YYYY')}
            </p>
            <p className="textProfileBlock">Number of Recipes Added:</p>
            <p className="textProfileBlock">{recipeData?.length}</p>
          </>
        ) : null}
        {isFreePlan(user) && !noRecipesYet ? (
          <>
            {!isEmpty(advertising?.[0]?.link) ? (
              <a href={advertising?.[0]?.link}>
                <img src={advertising?.[0]?.image} alt="" className="advertisingDesktop" />
              </a>
            ) : null}
            {!isEmpty(advertising?.[1]?.link) ? (
              <a href={advertising?.[1]?.link}>
                <img src={advertising?.[1]?.image} alt="" className="advertisingMobile" />
              </a>
            ) : null}
          </>
        ) : null}
      </div>
    )
  }

  return (
    <Layout title="Profile">
      <div className="containerMain containRecipe">
        {savedRecipe ? <BookProgress recipeId={savedRecipe} /> : null}
        {noRecipesYet ? (
          <FirstRecipeStart
            welcome={!!welcome}
            firstName={
              String(user?.fullname || '')
                .trim()
                .split(/\s+/)[0]
            }
            family={invitedFamily}
          />
        ) : null}
        <div className="dashboardWrap">
          <UserInfoBlock />
          <ListRecipes
            onClick={paginateRecipes}
            title="Recipes"
            innerRecipes="innerDraftRecipePadding"
            loadBtn={visibleLoad}
            visibleButtonAddRecipe={isNil(idUser)}
          >
            {!isEmpty(recipeData) ? (
              recipeData?.map(({ id, image, name, slug, locked }) => (
                <Link
                  to={checkLinkToRecipe(id, slug)}
                  key={id}
                  className={`innerRecipe ${locked ? 'lockedRecipe' : ''}`}
                >
                  <img
                    src={image ? image : '/images/imageRecipeWithoutPhoto.png'}
                    alt=""
                    className="recipeImage"
                  />
                  {locked ? (
                    <span className="recipeLockBadge">
                      <LockIcon /> Locked
                    </span>
                  ) : null}
                  <p className="nameRecipe">{name}</p>
                  <div className="darkWrapper" />
                </Link>
              ))
            ) : videoData?.url ? (
              <div className="profileVideo">
                <p className="textEmptyRecipes">{videoData?.text}</p>
                <video className="innerVideoForm" playsInline controls src={videoData?.url} />
              </div>
            ) : null}
          </ListRecipes>
        </div>
      </div>
    </Layout>
  )
}

export default connect(({ user }) => ({ user: user.data }))(Profile)
